import { createClient, SupabaseClient, AuthError } from '@supabase/supabase-js';

/**
 * Unified Supabase Client - Works for both Web and Mobile
 *
 * Platform-aware auth storage:
 * - Web: Uses browser's localStorage (default)
 * - Mobile: Uses AsyncStorage from @react-native-async-storage/async-storage
 *
 * Uses lazy initialization - client is created only when first accessed
 */

/**
 * Check if running on React Native
 */
function isReactNative(): boolean {
  return typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
}

/**
 * Get Supabase configuration from environment variables
 * Checks multiple sources:
 * 1. Expo Constants.expoConfig.extra (works in Expo web)
 * 2. process.env.EXPO_PUBLIC_* (standard Expo)
 * 3. process.env.REACT_APP_* (CRA web)
 */
function getSupabaseConfig(): { url: string; key: string } {
  // Only try Expo Constants in React Native/mobile environment
  // This check prevents webpack from trying to resolve expo-constants in web builds
  if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    try {
      // Use a variable to prevent webpack from statically analyzing the import
      const moduleName = 'expo-constants';
      const Constants = require(moduleName);
      if (Constants?.expoConfig?.extra) {
        const extra = Constants.expoConfig.extra as any;
        const url = extra.supabaseUrl || extra.EXPO_PUBLIC_SUPABASE_URL || '';
        const key = extra.supabaseAnonKey || extra.EXPO_PUBLIC_SUPABASE_ANON_KEY || '';

        if (url && key) {
          console.log('[Supabase] Using Expo Constants');
          return { url, key };
        }
      }
    } catch {
      // expo-constants not available
    }
  }

  // Second try: Expo environment variables
  const expoUrl = process.env.EXPO_PUBLIC_SUPABASE_URL || '';
  const expoKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '';

  if (expoUrl && expoKey) {
    console.log('[Supabase] Using Expo environment variables');
    return { url: expoUrl, key: expoKey };
  }

  // Third try: CRA variables (web) - fallback
  const reactUrl = process.env.REACT_APP_SUPABASE_URL || '';
  const reactKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_OR_ANON_KEY || '';

  if (reactUrl && reactKey) {
    console.log('[Supabase] Using CRA environment variables');
    return { url: reactUrl, key: reactKey };
  }

  console.error('[Supabase] No environment variables found');
  return { url: '', key: '' };
}

/**
 * Create AsyncStorage adapter for mobile
 */
function createAsyncStorageAdapter() {
  const storageModuleName = '@react-native-async-storage/async-storage';

  return {
    getItem: async (key: string): Promise<string | null> => {
      try {
        // @ts-ignore - AsyncStorage is a mobile-only dependency
        const AsyncStorage = await import(storageModuleName);
        return await AsyncStorage.default.getItem(key);
      } catch (error) {
        console.error('[Supabase Storage] getItem error:', error);
        return null;
      }
    },
    setItem: async (key: string, value: string): Promise<void> => {
      try {
        // @ts-ignore - AsyncStorage is a mobile-only dependency
        const AsyncStorage = await import(storageModuleName);
        await AsyncStorage.default.setItem(key, value);
      } catch (error) {
        console.error('[Supabase Storage] setItem error:', error);
      }
    },
    removeItem: async (key: string): Promise<void> => {
      try {
        // @ts-ignore - AsyncStorage is a mobile-only dependency
        const AsyncStorage = await import(storageModuleName);
        await AsyncStorage.default.removeItem(key);
      } catch (error) {
        console.error('[Supabase Storage] removeItem error:', error);
      }
    },
  };
}

/**
 * Check if an error is an invalid refresh token error
 */
export function isInvalidRefreshTokenError(error: unknown): boolean {
  if (error instanceof AuthError) {
    return (
      error.message?.includes('Invalid Refresh Token') ||
      error.message?.includes('Refresh Token Not Found') ||
      error.message?.includes('Already Used') ||
      error.code === 'session_not_found'
    );
  }
  if (error instanceof Error) {
    return (
      error.message?.includes('Invalid Refresh Token') ||
      error.message?.includes('Refresh Token Not Found') ||
      error.message?.includes('Already Used')
    );
  }
  return false;
}

/**
 * Clear session on auth error
 */
export async function clearSessionOnAuthError(): Promise<void> {
  console.log('[Supabase] Clearing session due to auth error');
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.warn('[Supabase] signOut error:', error.message);
    }
  } catch (error) {
    console.error('[Supabase] Error clearing session:', error);
  }
}

/**
 * Wrapper for operations with auth retry
 */
export async function withAuthRetry<T>(
  operation: () => Promise<T>,
  maxRetries: number = 2
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (isInvalidRefreshTokenError(error) && attempt < maxRetries) {
        console.log(`[Supabase] Auth error, retrying (${attempt + 1}/${maxRetries})`);
        await clearSessionOnAuthError();
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      throw error;
    }
  }

  throw lastError;
}

/**
 * Observer del tráfico a Supabase (sólo mobile lo registra, desde
 * SQLiteSyncAdapter): `true` cuando un request obtuvo respuesta HTTP,
 * `false` cuando falló por red.
 */
let supabaseTrafficObserver: ((online: boolean) => void) | null = null;

export function setSupabaseTrafficObserver(fn: ((online: boolean) => void) | null): void {
  supabaseTrafficObserver = fn;
}

/**
 * Create the Supabase client
 */
function createSupabaseClient(): SupabaseClient {
  const { url, key } = getSupabaseConfig();
  _supabaseUrl = url.replace(/\/+$/, '');

  if (!url || !key) {
    console.error(
      '[Supabase] Missing environment variables. ' +
      'Web: REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_PUBLISHABLE_OR_ANON_KEY. ' +
      'Mobile: EXPO_PUBLIC_SUPABASE_URL, EXPO_PUBLIC_SUPABASE_ANON_KEY.'
    );

    return new Proxy({} as SupabaseClient, {
      get: () => {
        throw new Error('Supabase client not configured. Check .env variables.');
      },
    });
  }

  if (isReactNative()) {
    // Mobile: Use AsyncStorage
    console.log('[Supabase] Creating client for React Native');
    return createClient(url, key, {
      auth: {
        storage: createAsyncStorageAdapter() as any,
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
      },
      // Observador de conectividad (mobile): toda respuesta del server = hay
      // internet; toda falla de red = no lo hay. El SQLiteSyncAdapter se
      // registra como observer (setSupabaseTrafficObserver) y usa esto como
      // sensor principal, evitando sondeos periódicos con la app online. Se
      // resuelve EN CADA request para que el orden de inicialización no
      // importe (el cliente es lazy pero puede crearse antes que el adapter).
      global: {
        fetch: (input, init) =>
          globalThis.fetch(input, init).then(
            (res) => { supabaseTrafficObserver?.(true); return res; },
            (err) => { supabaseTrafficObserver?.(false); throw err; }
          ),
      },
    });
  } else {
    // Web: Use localStorage (default)
    console.log('[Supabase] Creating client for Web');
    return createClient(url, key, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
  }
}

// Lazy initialization
let _supabase: SupabaseClient | null = null;
/** URL de la instancia con la que se creó el cliente (sin barra final). */
let _supabaseUrl = '';

function getSupabaseClient(): SupabaseClient {
  if (_supabase) {
    return _supabase;
  }
  _supabase = createSupabaseClient();
  return _supabase;
}

// Export a proxy that initializes lazily
export const supabase = new Proxy({} as SupabaseClient, {
  get: function(_target, prop) {
    const client = getSupabaseClient();
    return client[prop as keyof SupabaseClient];
  }
});

/**
 * URL de la instancia de Supabase que usa el cliente (la misma resolución de
 * config que `supabase`: Expo Constants / EXPO_PUBLIC_* / REACT_APP_*). Para
 * armar URLs públicas de Storage sin depender de variables de una sola
 * plataforma. '' si no hay config.
 */
export function getSupabaseUrl(): string {
  getSupabaseClient();
  return _supabaseUrl;
}

export default supabase;
